import {
    getSafe,
    isDefined,
    toNumber
} from "../../../services/common/object-util";
import * as EnumUtils from "../../common/utils/enum-utils";
import { getByCode } from "../../common/utils/enum-utils";
import TiebreakType from "../enums/tiebreak-type";
import TieBreakType from "../enums/tiebreak-type";
import GameType, { isUpTo } from "../enums/game-type";
import IncompleteType from "../enums/incomplete-type";
import MatchTiebreakType from "../enums/match-tiebreak-type";
import Ruleset from "../enums/ruleset";
import MatchResultType from "../enums/match-result-type";
import TennisTeamSide from "../enums/tennis-team-side";
import SetType from "../enums/set-type";

export const isSetDisabled = (set, match, gameConfiguration) => {
    const { code, value: setsToPlay } = getGameType(match, gameConfiguration);
    const startingDisabledIndex = Math.ceil(setsToPlay / 2);

    if (!isUpTo(code) && set.index >= startingDisabledIndex) {
        if (!set.away.score && !set.home.score) {
            const previousSets = match.sets.filter(
                (_, i) => i < startingDisabledIndex
            );
            const setsComplete = previousSets.every(
                (s) => isDefined(s.away.score) && isDefined(s.home.score)
            );

            if (!setsComplete) {
                return true;
            }

            return getMatchResult(match, gameConfiguration);
        }
    }

    return false;
};

export const getTiebreakAdjustedSet = (
    existingSet,
    updateSet,
    opponentSet,
    setType,
    match,
    gameConfiguration
) => {
    const isTiebreakSet = setType === SetType.TiebreakSets.code;
    const maxGames = getMaxGamesPerSet(match, gameConfiguration);
    const maxTiebreak = isTiebreakSet
        ? Math.max(0, opponentSet.tiebreak) + 1
        : getMaxTieBreakPoints(match, gameConfiguration);

    const { tiebreak: oldTiebreak } = existingSet;
    const { score, tiebreak: newTiebreak } = updateSet;

    const lostTb = oldTiebreak >= maxTiebreak && newTiebreak < maxTiebreak;
    const wonTb = oldTiebreak < maxTiebreak && newTiebreak >= maxTiebreak;

    if (lostTb && score) {
        return { ...updateSet, score: score - 1 };
    }
    if (wonTb && score <= maxGames) {
        return { ...updateSet, score: score + 1 };
    }
    return updateSet;
};

export const isSetTiebreakEligible = (set, match, gameConfiguration) => {
    const maxGamesPerSet = getMaxGamesPerSet(match, gameConfiguration);
    const { away, home } = set;
    const awaySetGames = toNumber(away.score);
    const homeSetGames = toNumber(home.score);

    return (
        awaySetGames &&
        homeSetGames &&
        [away.score, home.score].some((score) => score >= maxGamesPerSet)
    );
};

export const getAdditionalGames = (ruleSetId) => {
    switch (ruleSetId) {
        case Ruleset.DefaultTennis.code:
            return 1;
        default:
            return 0;
    }
};

export const getMaxGamesPerSet = (match, gameConfiguration) => {
    const { singles, doubles } = gameConfiguration;

    return getSafe(() =>
        match.isSingle ? singles.gamesPerSet : doubles.gamesPerSet
    );
};

export const getMaxTieBreakPoints = (match, gameConfiguration) => {
    const { singles, doubles } = gameConfiguration;

    const tiebreakType = getSafe(() =>
        match.isSingle ? singles.tieBreakType : doubles.tieBreakType
    );

    return EnumUtils.getByCode(TiebreakType, tiebreakType).points;
};

export const getGameType = (match, gameConfiguration) => {
    const { singles, doubles } = gameConfiguration || {};
    const gameTypeCode = getSafe(() =>
        match.isSingle ? singles.gameType : doubles.gameType
    );

    return EnumUtils.getByCode(GameType, gameTypeCode);
};

export const getSetWinner = (set, gamesPerSet, gamesPerTieBreak) => {
    let winner;

    const { away, home } = set;
    const awaySetGames = toNumber(away.score);
    const homeSetGames = toNumber(home.score);

    if (awaySetGames >= gamesPerSet || homeSetGames >= gamesPerSet) {
        if (awaySetGames === homeSetGames) {
            const awayTiebreak = toNumber(away.tiebreak);
            const homeTiebreak = toNumber(home.tiebreak);
            if (awayTiebreak !== homeTiebreak) {
                if (awayTiebreak === gamesPerTieBreak) {
                    winner = TennisTeamSide.Away.code;
                } else if (homeTiebreak === gamesPerTieBreak) {
                    winner = TennisTeamSide.Home.code;
                }
            }
        } else if (awaySetGames > homeSetGames) {
            winner = TennisTeamSide.Away.code;
        } else if (homeSetGames > awaySetGames) {
            winner = TennisTeamSide.Home.code;
        }
    }

    return winner;
};

export const isMatchTied = (match, gameConfiguration) => {
    return (
        getMatchResult(match, gameConfiguration) === MatchResultType.Tie.code
    );
};

const getTotalSetsWon = (sets, gamesPerSet, gamesPerTieBreak, teamSide) => {
    return sets.reduce((sum, set) => {
        return getSetWinner(set, gamesPerSet, gamesPerTieBreak) === teamSide
            ? sum + 1
            : sum;
    }, 0);
};

/**
 *
 * @param {Match} match
 * @param {*} gameConfiguration
 * @param {string} teamSide
 * @returns {{total: number, sets: number, tbSets: number, tbPoints: number}}
 */
export const getMatchScore = (match, gameConfiguration, teamSide) => {
    const { sets, tiebreakSets, isSingle, matchTiebreakType } = match;
    const { gamesPerSet, tieBreakType } = isSingle
        ? gameConfiguration.singles
        : gameConfiguration.doubles;
    const gamesPerTieBreak = getByCode(TieBreakType, tieBreakType).points;

    const setsWon = getTotalSetsWon(
        sets,
        gamesPerSet,
        gamesPerTieBreak,
        teamSide
    );

    let tbSets = 0;
    let tbPoints = 0;

    if (matchTiebreakType === MatchTiebreakType.Sets.code) {
        tbSets += getTotalSetsWon(
            tiebreakSets,
            gamesPerSet,
            gamesPerTieBreak,
            teamSide
        );
    }

    if (matchTiebreakType === MatchTiebreakType.Points.code) {
        const { away, home } = match.tiebreakPoints || { away: 0, home: 0 };
        if (
            (teamSide === TennisTeamSide.Away.code &&
                toNumber(away) > toNumber(home)) ||
            (teamSide === TennisTeamSide.Home.code &&
                toNumber(home) > toNumber(away))
        ) {
            tbPoints++;
        }
    }

    return {
        total: setsWon + tbSets + tbPoints,
        sets: setsWon,
        tbSets,
        tbPoints
    };
};

const getSetsPerMatch = (match, gameConfiguration) => {
    const { isSingle } = match;
    const { gameType } = isSingle
        ? gameConfiguration.singles
        : gameConfiguration.doubles;

    return getByCode(GameType, gameType).value;
};

export const getMatchResult = (match, gameConfiguration) => {
    const incompleteWinner = getIncompleteWinner(match.home, match.away);
    if (incompleteWinner) {
        return incompleteWinner;
    } else if (match.away.noPlayer && match.home.noPlayer) {
        return MatchResultType.Tie.code;
    } else if (match.away.noPlayer || match.home.noPlayer) {
        return match.away.noPlayer
            ? MatchResultType.Home.code
            : MatchResultType.Away.code;
    }

    const setsPerMatch = getSetsPerMatch(match, gameConfiguration);
    const {
        total: awayScore,
        tbPoints: awayTbPoints,
        tbSets: awayTbSets
    } = getMatchScore(match, gameConfiguration, TennisTeamSide.Away.code);
    const {
        total: homeScore,
        tbPoints: homeTbPoints,
        tbSets: homeTbSets
    } = getMatchScore(match, gameConfiguration, TennisTeamSide.Home.code);

    const isWinningSetsReached =
        awayScore > setsPerMatch / 2 || homeScore > setsPerMatch / 2;
    const isWinningMatchTiebreak =
        homeTbPoints || awayTbPoints || awayTbSets !== homeTbSets;

    const isMatchComplete = isWinningSetsReached || isWinningMatchTiebreak;

    if (isMatchComplete) {
        if (awayScore === homeScore) {
            return MatchResultType.Tie.code;
        } else if (awayScore > setsPerMatch / 2) {
            return MatchResultType.Away.code;
        } else if (homeScore > setsPerMatch / 2) {
            return MatchResultType.Home.code;
        }
        if (isWinningMatchTiebreak) {
            if (awayScore > homeScore) {
                return MatchResultType.Away.code;
            } else if (homeScore > awayScore) {
                return MatchResultType.Home.code;
            }
        }
    }

    return null;
};

export const isSetComplete = (set, match, gameConfiguration) => {
    const { isSingle } = match;
    const { gamesPerSet, tieBreakType } = isSingle
        ? gameConfiguration.singles
        : gameConfiguration.doubles;
    const gamesPerTieBreak = getByCode(TieBreakType, tieBreakType).points;

    return getSetWinner(set, gamesPerSet, gamesPerTieBreak);
};

const getMatchTeamStats = (teamSide, match, gameConfiguration) => {
    const { isSingle } = match;
    const { gamesPerSet, tieBreakType } = isSingle
        ? gameConfiguration.singles
        : gameConfiguration.doubles;
    const gamesPerTieBreak = getByCode(TieBreakType, tieBreakType).points;

    return match.sets.reduce(
        (acc, set) => {
            const { score, tiebreak } = set[teamSide];
            const setWinner = getSetWinner(set, gamesPerSet, gamesPerTieBreak);
            acc.gamesWon += toNumber(score) + toNumber(tiebreak);
            acc.setsWon += setWinner === teamSide ? 1 : 0;

            return acc;
        },
        { gamesWon: 0, setsWon: 0 }
    );
};

export const getMatchStats = (match, gameConfiguration) => {
    return {
        away: getMatchTeamStats(
            TennisTeamSide.Away.code,
            match,
            gameConfiguration
        ),
        home: getMatchTeamStats(
            TennisTeamSide.Home.code,
            match,
            gameConfiguration
        )
    };
};

const getIncompleteWinner = (home, away) => {
    const isTie =
        IncompleteType.DNF.equals(home.incompleteType) ||
        IncompleteType.DNF.equals(away.incompleteType) ||
        IncompleteType.NotPlayed.equals(home.incompleteType) ||
        IncompleteType.NotPlayed.equals(away.incompleteType) ||
        (IncompleteType.DQ.equals(home.incompleteType) &&
            IncompleteType.DQ.equals(away.incompleteType));

    const singleWinnerTypes = [
        IncompleteType.Forfeit,
        IncompleteType.DQ,
        IncompleteType.Retired
    ];

    if (isTie) {
        return MatchResultType.Tie.code;
    } else if (
        singleWinnerTypes.some(
            (type) =>
                type.equals(home.incompleteType) ||
                type.equals(away.incompleteType)
        )
    ) {
        return home.incompleteType
            ? MatchResultType.Away.code
            : MatchResultType.Home.code;
    } else {
        return null;
    }
};
